import { axios, isAxiosError } from "@root/libs/axios";
import { showAlert, getMobileAppState } from "@root/shared/utils";
import { navigateTo } from "@root/shared/utils/navigateTo";
import { sendAppWebhook } from "@userFrontend/utils/sendAppWebhook";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { IOS_APP_ID, ANDROID_APP_ID } from "@root/shared/utils/constants";
import { type ViolationReportFormData, type ViolationType, type ErrorResponse } from "./type";

// 送信完了時に送るwebhookの内容
const APP_EVENT_MESSAGE = {
  message: "ViolationReport",
};

// Rails側バリデーションエラーのキーをフロントエンドのフィールド名に変換
const mapErrorKey = (key: string): string => {
  const keyMap: Record<string, string> = {
    violation: "violationType",
    violationOtherDetail: "violationTypeOtherDetail",
    client: "clientId",
    clientId: "clientId",
    jobEntryId: "jobEntryId",
    personInCharge: "personInChargeName",
    personInChargeDetails: "personInChargeDetails",
    email: "alternativeEmail",
    alternativeEmail: "alternativeEmail",
    documentsSubmittable: "documentsSubmittable",
  };

  return keyMap[key] || key;
};

/**
 * フォームデータをRailsに送信
 * @param data フォームデータ
 * @param violationTypes 違反理由の選択肢
 * @param registeredEmail 登録済みメールアドレス
 * @param completedRedirectPath 完了後のリダイレクト先パス
 * @param setServerErrors サーバーエラーを設定するコールバック関数
 * @param scrollToError エラー箇所にスクロールする関数
 */
export const submitViolationReport = async ({
  postUrl,
  data,
  violationTypes,
  registeredEmail,
  completedRedirectPath,
  setServerErrors,
  scrollToError,
}: {
  postUrl: string;
  data: ViolationReportFormData;
  violationTypes: ViolationType[];
  registeredEmail: string;
  completedRedirectPath: string;
  setServerErrors?: (errors: Record<string, string>[]) => void;
  scrollToError?: (errorKey: string) => void;
}): Promise<void> => {
  // 型安全性を確保するためにプロパティを取り出す
  const { violationType } = data;
  const { violationTypeOtherDetail } = data;
  const { personInChargeName } = data;
  const { personInChargeDetails } = data;
  const { alternativeEmail } = data;

  // 違反行為の疑いがある行為（配列形式で送信）
  const violations = violationType && violationType.length > 0 ? violationType : [];

  // その他の詳細（「その他」が選択されている場合のみ）
  const foundOtherOption = violationTypes.find((r) => r.optionText.includes("その他"));
  const otherDetail =
    foundOtherOption && violationType.includes(foundOtherOption.optionId)
      ? (violationTypeOtherDetail ?? "")
      : undefined;

  // 入社日（任意）YYYY-MM-DD形式で送信
  const employmentDate: string | undefined = data.employmentDate
    ? (() => {
        const year = data.employmentDate.getFullYear();
        const month = String(data.employmentDate.getMonth() + 1).padStart(2, "0");
        const day = String(data.employmentDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      })()
    : undefined;

  // axiosで送信するデータを構築
  const postData = {
    userReportedViolation: {
      violation: violations,
      ...(otherDetail && { violationOtherDetail: otherDetail }),
      ...(data.clientId && { clientId: data.clientId }),
      ...(data.clientShopId && { clientShopId: data.clientShopId }),
      ...(data.jobEntryId && { jobEntryId: data.jobEntryId }),
      ...(personInChargeName && { personInCharge: personInChargeName }),
      ...(personInChargeDetails && { personInChargeDetails }),
      ...(employmentDate && { employmentDate }),
      documentsSubmittable: data.documentsSubmittable ?? false,
      email: alternativeEmail || registeredEmail,
    },
  };

  try {
    await axios.post(postUrl, postData);
    // 投稿完了
    const mobileAppDataNum = getMobileAppState();
    // アプリwebviewの場合はwebhookを送り、リダイレクトはしない
    if (mobileAppDataNum === IOS_APP_ID || mobileAppDataNum === ANDROID_APP_ID) {
      sendAppWebhook({
        iosEventMessage: APP_EVENT_MESSAGE,
        androidEventName: "ViolationReport",
        androidEventMessage: JSON.stringify(APP_EVENT_MESSAGE),
      });
      return;
    }
    // 通常ブラウザの場合はリダイレクト
    navigateTo(completedRedirectPath);
  } catch (error) {
    if (!isAxiosError(error) || !error.response) {
      showAlert("送信に失敗しました。時間をおいて再度お試しください。");
      return;
    }

    const errorResponse: ErrorResponse = error.response as ErrorResponse;
    switch (errorResponse.status) {
      case 400:
        if (errorResponse.data.errors) {
          // Rails側バリデーションエラーを親コンポーネントに伝える
          if (setServerErrors) {
            setServerErrors(errorResponse.data.errors);
          }

          // 最初のエラー箇所にスクロール
          const firstErrorKey: string = Object.keys(errorResponse.data.errors[0])[0];

          if (firstErrorKey && scrollToError) {
            // エラーメッセージをもつ最初のキーの項目へスクロールする
            scrollToError(mapErrorKey(firstErrorKey));
          }
        }
        break;
      default:
        sendErrorLog(error);
        showAlert("送信に失敗しました。時間をおいて再度お試しください。");
    }
  }
};
